import { createSelector } from '@reduxjs/toolkit';
import { RECORD_TYPE } from 'LixaniAPI/enums';
import dayjs from 'dayjs';
import {
  selectProjectsArray,
  selectSelectedProject,
  selectSelectedProjectId,
} from 'store/projects/selectors/projectsSelectors';
import {
  selectSelectedProjectAndChildProjectRecords,
  selectSelectedRecordsIds,
} from 'store/records/selectors/records.selectors';
import sort from 'utils/sort';
import returnProjectIdAndItsChildProjectIds from '../../utils/returnProjectIdAndItsChildProjectIds';
import {
  selectAllAssignments,
  selectAllFilePermissions,
} from './workDemoSliceSelectors';

import { orderBy, uniq, uniqBy, uniqWith } from 'lodash-es';
import {
  EDIT_OTHER_USERS_RECORDS,
  RECORDS_EDIT_OWN_RECORDS,
} from 'scenes/Permissions/constants/permissions';
import {
  getNearestUserProjectPermissions,
  selectResourceDefaultPermissions,
  selectUserProjectPermissions,
} from 'scenes/Permissions/store/selectors/personalPermissionsSelectors';
import { selectCurrentUser } from 'store/selectors/root.selectors';

import { selectUserAdminBusinessIds } from 'scenes/Permissions/store/selectors/base-project-permissions.selectors';
import { uniqueResourcesByUserAndEmployer } from 'scenes/WorkDemo/utils/uniqueResourcesByUserAndEmployer';
import { selectEmployeeBusinessConfigurationsByBusinessId } from 'store/projectUsers/selectors/projectUsers.selectors';
import { selectProjectsThatUserHasResourceToOrIsCreatorOf } from 'store/projects/selectors/userProjects.selectors';
import { selectPersonalRecordsWithAllowanceIds } from 'store/recordAllowances/selectors/record-allowances.selectors';
import { selectWorkCalendarRecordsArray } from 'store/records/selectors/records-base.selectors';
import {
  selectCurrentUserEmployerProjects,
  selectLoggedInUserResourcesArray,
} from 'store/resources/selectors/resources.selectors';
import { selectUserActiveEmployerId } from 'store/user/selectors.js/user.selectors';
import { arrayToMap } from 'utils/arrayToMap';
import {
  selectIsPersonalWorkCalendarMode,
  selectWorkCalendarEmployers,
  selectWorkCalendarEmployersArray,
  selectWorkCalendarProjectItemsArray,
  selectWorkCalendarResourcesArray,
} from './base-work-calendar.selectors';
import {
  selectSelectedApprovalGroupCodes,
  selectSelectedAssignmentIds,
  selectSelectedEmployersBusinessIds,
  selectSelectedProjectIds,
  selectSelectedRecordTypeFilterOptions,
  selectSelectedResourceIds,
  selectSelectedUserIds,
  selectWorkSearchParamsDescriptionSearchText,
  selectWorkSearchParamsFromDate,
  selectWorkSearchParamsToDate,
  selectWorkSearchParamsVisibleWeeks,
  selectWorkSearchStateFromDateAndToDate,
} from './workSearchParamsSelectors';

export const selectWorkCalendarProjectsArray = createSelector(
  [
    selectWorkCalendarProjectItemsArray,
    selectIsPersonalWorkCalendarMode,
    selectProjectsArray,
  ],
  (workCalendarProjects, isPersonalWorkCalendarMode, projects) => {
    if (isPersonalWorkCalendarMode) {
      return projects;
    }

    if (workCalendarProjects) {
      return workCalendarProjects;
    } else {
      return [];
    }
  }
);

const selectProjects = createSelector(
  [selectWorkCalendarProjectsArray],
  (projects) => {
    return arrayToMap(projects);
  }
);

export const selectEmployerProjectsArray = createSelector(
  [
    selectWorkCalendarEmployersArray,
    selectIsPersonalWorkCalendarMode,
    selectCurrentUserEmployerProjects,
  ],
  (workCalendarEmployers, isPersonalWorkCalendarMode, personalEmployers) => {
    if (isPersonalWorkCalendarMode) {
      return personalEmployers;
    }
    if (workCalendarEmployers) {
      return workCalendarEmployers;
    } else {
      return [];
    }
  }
);

const selectEmployerProjectEntities = createSelector(
  [selectEmployerProjectsArray],
  (employersArray) => {
    return arrayToMap(employersArray);
  }
);

export const selectProjectsDataResourcesArray = createSelector(
  [
    selectWorkCalendarResourcesArray,
    selectIsPersonalWorkCalendarMode,
    selectLoggedInUserResourcesArray,
  ],
  (workCalendarResources, isPersonalWorkCalendarMode, personalResources) => {
    if (isPersonalWorkCalendarMode) {
      return personalResources;
    }
    if (workCalendarResources) {
      return workCalendarResources;
    } else {
      return [];
    }
  }
);

export const selectProjectsDataResourceById = createSelector(
  [selectProjectsDataResourcesArray, (_, resourceId) => resourceId],
  (resources, resourceId) =>
    resources.find((resource) => resource.id === resourceId)
);

const selectProjectsDataResourceEntities = createSelector(
  [selectProjectsDataResourcesArray],
  (resourcesArray) => {
    return arrayToMap(resourcesArray);
  }
);

const selectRecordsWhichResourceHasSelectedProjectAsEmployer = createSelector(
  [
    selectWorkCalendarRecordsArray,
    selectSelectedProject,
    selectProjectsDataResourceEntities,
  ],
  (records, selectedProject, resources) => {
    return records.filter((record) => {
      const recordsResource = resources[record.resource];

      return (
        recordsResource &&
        recordsResource.employerBusinessId &&
        recordsResource.employerBusinessId === selectedProject.business_id
      );
    });
  }
);

export const selectAssignmentsArray = createSelector(
  [selectAllAssignments],
  (assignments) => Object.values(assignments)
);

export const selectAssignmentsArrayWithoutDeleted = createSelector(
  [selectAssignmentsArray],
  (assignments) => assignments.filter((assignment) => !assignment.deletedAt)
);

const selectSelectedProjectAndChildProjectsFilePermissionsArray =
  createSelector(
    [
      selectAllFilePermissions,
      selectWorkCalendarProjectsArray,
      selectEmployerProjectsArray,
      selectSelectedProjectId,
    ],
    (filePermissions, projects, employers, selectedProjectId) => {
      const allProjects = [...projects, ...employers];

      const projectAndChildProjects = returnProjectIdAndItsChildProjectIds(
        [selectedProjectId],
        allProjects
      );

      return filePermissions.filter((fp) =>
        projectAndChildProjects.includes(fp.project)
      );
    }
  );

const selectLoggedInUserFilePermissionsArray = createSelector(
  [selectAllFilePermissions, selectCurrentUser],
  (filePermissions, user) => {
    const { firstName, lastName } = user.resource;
    return filePermissions
      .filter((filePermission) => filePermission.user === user.id)
      .map((filePermission) => ({
        ...filePermission,
        user: { firstName, lastName, id: user.id, resource: user.resource },
      }));
  }
);

export const selectProjectsFilePermissionsInTimeFrame = createSelector(
  [
    selectWorkSearchStateFromDateAndToDate,
    selectSelectedProjectAndChildProjectsFilePermissionsArray,
  ],
  (dates, filePermissions) => {
    const fromDate = dayjs(dates.fromDate);
    const toDate = dayjs(dates.toDate);

    return filePermissions.filter((filePermission) => {
      const createdAt = dayjs(filePermission.createdAt);
      const isBeforeOrSameDay =
        createdAt.isBefore(toDate) || createdAt.isSame(toDate, 'day');
      const isAfterOrSameDay =
        createdAt.isAfter(fromDate) || createdAt.isSame(fromDate, 'day');

      return isBeforeOrSameDay && isAfterOrSameDay;
    });
  }
);

export const selectProjectsImagesAndVideosInTimeFrame = createSelector(
  [selectProjectsFilePermissionsInTimeFrame],
  (filePermissions) => {
    const fps = filePermissions.filter((fp) => fp.file && fp.file?.mimeType);
    const images = fps.filter((filePermission) =>
      filePermission.file.mimeType.includes('image')
    );
    const videos = fps.filter((filePermission) =>
      filePermission.file.mimeType.includes('video')
    );

    return { images, videos };
  }
);

export const selectCurrentUserFilePermissionsForDay = createSelector(
  [
    selectLoggedInUserFilePermissionsArray,
    selectWorkCalendarProjectsArray,
    (_, args) => args,
  ],
  (filePermissions, projects, args) => {
    const { day } = args;
    const dayFiles = filePermissions.filter(
      (filePermission) =>
        filePermission.type === 'FILE' &&
        day.format('YYYYMMDD') ===
          dayjs(filePermission.createdAt).format('YYYYMMDD') &&
        filePermission.file?.mimeType
    );

    const dayFilesWithProjectData = dayFiles.map((file) => {
      const fileProject = projects.find(
        (project) => project.id === file.project
      );
      return {
        ...file,
        project: fileProject ? fileProject : file.project,
      };
    });

    const mediaFiles = dayFilesWithProjectData.filter(
      (filePermission) =>
        filePermission.file.mimeType.includes('image') ||
        filePermission.file.mimeType.includes('video')
    );

    const documentFiles = dayFilesWithProjectData.filter(
      (filePermission) =>
        filePermission.file.mimeType.includes('application') ||
        filePermission.file.mimeType.includes('text')
    );

    return { mediaFiles, documentFiles };
  }
);

export const selectUsersFilePermissionsForDay = createSelector(
  [
    selectSelectedProjectAndChildProjectsFilePermissionsArray,
    (_, args) => args,
  ],
  (filePermissions, args) => {
    const { day, user } = args;
    const dayFiles = filePermissions.filter(
      (p) =>
        p.user === user &&
        p.type === 'FILE' &&
        day.format('YYYYMMDD') === dayjs(p.createdAt).format('YYYYMMDD') &&
        p.file?.mimeType
    );

    const mediaFiles = dayFiles.filter(
      (filePermission) =>
        filePermission.file.mimeType.includes('image') ||
        filePermission.file.mimeType.includes('video')
    );

    const documentFiles = dayFiles.filter(
      (filePermission) =>
        filePermission.file.mimeType.includes('application') ||
        filePermission.file.mimeType.includes('text')
    );

    return { mediaFiles, documentFiles };
  }
);

export const selectSelectedAssignmentOptions = createSelector(
  selectAssignmentsArrayWithoutDeleted,
  selectSelectedAssignmentIds,
  selectProjects,
  (assignments, selectedAssignmentIds, projects) => {
    const selectedAssignments = assignments.filter((assignment) =>
      selectedAssignmentIds.includes(assignment.id)
    );

    let options = selectedAssignments.map((assignment) => {
      if (assignment === null) {
        return {};
      }

      const assignmentProjectName =
        projects[assignment.project] && projects[assignment.project].name
          ? projects[assignment.project].name
          : '';
      return {
        id: assignment.id,
        value: assignment.id,
        parent: assignment.parent ? assignment.parent : false,
        label: `${nestedAssignmentNumber(assignment, assignments)}. ${
          assignment.name
        }`,
        type: assignmentProjectName,
        project: assignment.project,
      };
    });
    options.sort((a, b) => sort.sortByKey(a, b, 'label'));

    return options;
  }
);

export const selectSelectedEmployerOptions = createSelector(
  selectEmployerProjectsArray,
  selectSelectedEmployersBusinessIds,
  (employers, employerBusinessIds) => {
    const selectedEmployers = employers.filter((employer) =>
      employerBusinessIds.includes(employer.business_id)
    );

    let options = selectedEmployers.map((employer) => {
      return {
        value: employer.business_id,
        label: employer.name,
        type: 'selected',
      };
    });
    options.sort((a, b) => sort.sortByKey(a, b, 'label'));
    return uniqBy(options, 'value');
  }
);

export const selectSelectedProjectAndChildProjectAndResourceHasSelectedProjectAsEmployerRecords =
  createSelector(
    [
      selectSelectedProjectAndChildProjectRecords,
      selectRecordsWhichResourceHasSelectedProjectAsEmployer,
    ],
    (
      projectAndChildProjectsRecords,
      resourceHasSelectedProjectAsEmployerRecords
    ) => {
      let recordsUniqued = uniqBy(
        [
          ...projectAndChildProjectsRecords,
          ...resourceHasSelectedProjectAsEmployerRecords,
        ],
        'id'
      );
      return recordsUniqued;
    }
  );

export const selectResourcesEmployedBySelectedEmployer = createSelector(
  [selectProjectsDataResourcesArray, selectSelectedProject],
  (resources, selectedProject) => {
    if (selectedProject && selectedProject.businessTypeEnum === 'EMPLOYER') {
      return resources.filter(
        (resource) =>
          resource.employerBusinessId === selectedProject.business_id
      );
    } else {
      return [];
    }
  }
);

export const selectSelectedProjectAndChildProjects = createSelector(
  [selectWorkCalendarProjectsArray, selectSelectedProjectId],
  (projects, selectedProject) => {
    const childProjectIds = returnProjectIdAndItsChildProjectIds(
      [selectedProject],
      projects
    );

    const projectAndChildProjects = projects.filter((project) =>
      childProjectIds.includes(project.id)
    );
    return projectAndChildProjects;
  }
);

export const selectProjectsWhichResourceHasSelectedProjectAsEmployer =
  createSelector(
    [
      selectWorkCalendarProjectsArray,
      selectResourcesEmployedBySelectedEmployer,
    ],
    (projects, resources) => {
      const projectIdsOfResources = uniq(
        resources.map((resource) => resource.project)
      );

      return projects.filter((project) =>
        projectIdsOfResources.includes(project.id)
      );
    }
  );

export const selectProjectsOfSelectedProject = createSelector(
  [
    selectProjectsWhichResourceHasSelectedProjectAsEmployer,
    selectSelectedProjectAndChildProjects,
  ],
  (resourceProjects, projects) => {
    return [...resourceProjects, ...projects];
  }
);

export const selectRecordsBasedOnWorkCalendarMode = createSelector(
  [
    selectIsPersonalWorkCalendarMode,
    selectSelectedProjectAndChildProjectAndResourceHasSelectedProjectAsEmployerRecords,
    selectPersonalRecordsWithAllowanceIds,
  ],
  (isPersonalWorkCalendarMode, projectRecords, personalRecords) =>
    isPersonalWorkCalendarMode ? personalRecords : projectRecords
);

export const selectWorkCalendarFilteredRecords = createSelector(
  [
    selectRecordsBasedOnWorkCalendarMode,
    selectProjectsDataResourceEntities,
    selectWorkSearchParamsVisibleWeeks,
    selectSelectedEmployersBusinessIds,
    selectSelectedProjectIds,
    selectSelectedResourceIds,
    selectSelectedAssignmentIds,
    selectWorkSearchParamsFromDate,
    selectWorkSearchParamsToDate,
    selectWorkSearchParamsDescriptionSearchText,
    selectSelectedUserIds,
    selectSelectedApprovalGroupCodes,
    selectSelectedRecordTypeFilterOptions,
  ],
  (
    records,
    resources,
    visibleWeeks,
    selectedEmployerBusinessIds,
    selectedProjectIds,
    selectedResourceIds,
    selectedAssignmentIds,
    fromDate,
    toDate,
    descriptionSearchText,
    selectedUserIds,
    approvalGroupCodes,
    selectedRecordTypeFilterOptions
  ) => {
    const isInVisibleWeeks = (record) =>
      visibleWeeks.includes(dayjs(record.clockInAt).endOf('isoWeek').format());

    const isInSelectedTimeframe = (record) =>
      record.clockInAt > fromDate && record.clockInAt < toDate;
    const isInSelectedProjectsOrNoProjectsSelected = (record) =>
      selectedProjectIds.length === 0 ||
      selectedProjectIds.includes(record.project);
    const isInSelectedApprovalGroupCodes = (record) =>
      approvalGroupCodes.length === 0 ||
      approvalGroupCodes.includes(record.groupCode);

    const isInSelectedUsersOrNoUserSelected = (record) =>
      selectedUserIds.length === 0 ||
      selectedUserIds.includes(resources[record.resource]?.user);
    const isInSelectedEmployersOrNoEmployersSelected = (record) =>
      selectedEmployerBusinessIds.length === 0 ||
      selectedEmployerBusinessIds.includes(
        resources[record.resource]?.employerBusinessId
      );
    const isInSelectedResourcesOrNoResourcesSelected = (record) =>
      selectedResourceIds.length === 0 ||
      selectedResourceIds.includes(record.resource);
    const hasAnySelectedAssignmentOrNoAssignmentsSelected = (record) =>
      selectedAssignmentIds.length === 0 ||
      (selectedAssignmentIds.includes('noAssignment') && !record.assignment) ||
      selectedAssignmentIds.some((id) => id === record.assignment);
    const isNotDeleted = (record) =>
      record.deletedAt === null || !record.hasOwnProperty('deletedAt');

    const descriptionSeachTextsArray = descriptionSearchText
      ? descriptionSearchText.trim().split(',')
      : [];
    const descriptionSearchTextsTrimmed = descriptionSeachTextsArray
      .filter((text) => text)
      .map((text) => text.trim());

    const isSearchText = descriptionSearchTextsTrimmed.length > 0;

    const descriptionMatchesSearchText = (record) => {
      let isIncluded = false;
      descriptionSearchTextsTrimmed.forEach((text) => {
        if (
          record.description &&
          record.description.trim().toUpperCase().includes(text)
        ) {
          isIncluded = true;
        }
      });
      return isSearchText ? isIncluded : true;
    };

    const isOfSelectedRecordTypeFilterOptions = (record) => {
      // if this is a child record (allowance), check if parent record type is selected
      if (record.parentRecordId) {
        const parentRecord = records.find(
          (r) => r.id === record.parentRecordId
        );
        if (
          parentRecord &&
          selectedRecordTypeFilterOptions &&
          selectedRecordTypeFilterOptions.length > 0
        ) {
          return selectedRecordTypeFilterOptions.includes(parentRecord.type);
        }
        return true;
      }

      if (
        selectedRecordTypeFilterOptions &&
        selectedRecordTypeFilterOptions.length > 0
      ) {
        return selectedRecordTypeFilterOptions.includes(record.type);
      } else {
        return true;
      }
    };

    const timeframeRecords = records.filter(
      (record) =>
        isInVisibleWeeks(record) &&
        isInSelectedTimeframe(record) &&
        isInSelectedEmployersOrNoEmployersSelected(record) &&
        isInSelectedProjectsOrNoProjectsSelected(record) &&
        isInSelectedResourcesOrNoResourcesSelected(record) &&
        hasAnySelectedAssignmentOrNoAssignmentsSelected(record) &&
        isNotDeleted(record) &&
        descriptionMatchesSearchText(record) &&
        isInSelectedUsersOrNoUserSelected(record) &&
        isInSelectedApprovalGroupCodes(record) &&
        isOfSelectedRecordTypeFilterOptions(record)
    );

    return timeframeRecords;
  }
);

export const selectHighestLevelProjectFromSelectedProjects = createSelector(
  [selectSelectedProjectIds, selectProjectsArray],
  (selectedProjectIds, projects) => {
    if (selectedProjectIds.length === 0) return null;

    // Get the selected project objects
    const selectedProjects = projects.filter(
      (project) =>
        selectedProjectIds.includes(project.id) &&
        project.businessTypeEnum !== 'EMPLOYER'
    );

    if (selectedProjects.length === 0) return null;

    // Find the project with the least nesting (highest level)
    const getProjectLevel = (project) => {
      let level = 0;
      let current = project;

      while (current.parent) {
        level++;
        current = projects.find((p) => p.id === current.parent);
        if (!current) break;
      }

      return level;
    };

    // Sort by level (ascending = highest level first)
    const sortedByLevel = selectedProjects
      .map((project) => ({ project, level: getProjectLevel(project) }))
      .sort((a, b) => a.level - b.level);

    return sortedByLevel[0]?.project || null;
  }
);

export const selectProjectsDataResourcesOfWorkCalendarRecords = createSelector(
  [selectProjectsDataResourcesArray, selectWorkCalendarFilteredRecords],
  (projectsDataResources, workCalendarRecords) => {
    const workCalendarRecordsResourceIds = uniq(
      workCalendarRecords.map((record) => record.resource)
    );

    return projectsDataResources.filter((resource) =>
      workCalendarRecordsResourceIds.includes(resource.id)
    );
  }
);
export const selectProjectsDataResourcesArrayWithEmployeeConfigurationInformation =
  createSelector(
    [
      selectProjectsDataResourcesOfWorkCalendarRecords,
      selectEmployeeBusinessConfigurationsByBusinessId,
      selectSelectedEmployersBusinessIds,
      selectSelectedProject,
      selectUserActiveEmployerId,
      selectWorkCalendarEmployers,
    ],
    (
      resources,
      employeeConfigurations,
      selectedBusinessIds,
      selectedProject,
      activeEmployerId,
      employers
    ) => {
      const selectedNavEmployer = employers[activeEmployerId];

      const effectiveBusinessId =
        selectedBusinessIds[0] ||
        selectedNavEmployer?.business_id ||
        selectedProject.business_id;

      return resources.map((resource) => {
        const foundEmployeeConfigurationForResource = employeeConfigurations[
          effectiveBusinessId
        ]
          ? employeeConfigurations[effectiveBusinessId].find(
              (employeeConfiguration) =>
                employeeConfiguration.userId === resource.user
            )
          : undefined;

        return {
          ...resource,
          baseSalaryType: foundEmployeeConfigurationForResource
            ? foundEmployeeConfigurationForResource.baseSalaryType
            : 'HOURLY',
          personNumber: foundEmployeeConfigurationForResource
            ? foundEmployeeConfigurationForResource.procountorPersonNumber
            : '',
          salaryMonth: foundEmployeeConfigurationForResource?.salaryMonth,
          salaryHour: foundEmployeeConfigurationForResource?.salaryHour,
          monthSalaryFrequency:
            foundEmployeeConfigurationForResource?.monthSalaryFrequency,
          toolAllowance: foundEmployeeConfigurationForResource?.toolAllowance,
        };
      });
    }
  );

export const selectRecordsByWeekUserEmployerKey = createSelector(
  [
    selectWorkCalendarFilteredRecords,
    selectProjectsDataResourceEntities,
    selectEmployerProjectsArray,
    selectSelectedProject,
  ],
  (records, resources, employerProjects, selectedProject) => {
    const weekRecordsByUserEmployerKey = [];

    records.forEach((record) => {
      const foundRecordResource = resources[record.resource];

      if (foundRecordResource && foundRecordResource.user) {
        const employerKey = foundRecordResource.employerBusinessId;
        const employer = employerProjects.find(
          (employer) => employer.business_id === employerKey
        );
        const businessName = employer?.name || 'MyLixani';
        const isFromSelectedProjectBusiness =
          employerKey === selectedProject?.business_id ? '0' : '1';
        const firstName = foundRecordResource.firstName || '';

        const weekUserEmployerKey = `${dayjs(record.clockInAt)
          .endOf('isoWeek')
          .format()}/${isFromSelectedProjectBusiness}/${businessName}/${firstName}/${employerKey}/${
          foundRecordResource.user
        }`;

        weekRecordsByUserEmployerKey[weekUserEmployerKey] =
          weekRecordsByUserEmployerKey[weekUserEmployerKey] || [];
        weekRecordsByUserEmployerKey[weekUserEmployerKey].push(record);
      } else {
        if (process.env.NODE_ENV !== 'production') {
          console.log('record without resource or user', record);
        }
      }
    });

    return weekRecordsByUserEmployerKey;
  }
);

export const selectRecordsByBusinessId = createSelector(
  [
    selectWorkCalendarFilteredRecords,
    selectSelectedRecordsIds,
    selectProjectsDataResourceEntities,
    selectEmployerProjectsArray,
  ],
  (allRecords, selectedRecordsIds, resources, employersArray) => {
    const records =
      selectedRecordsIds.length > 0
        ? allRecords.filter((record) => selectedRecordsIds.includes(record.id))
        : allRecords;

    const employersBusinessIds = uniq([
      ...employersArray.map((employer) => employer.business_id),
      'MyLixani',
    ]);

    const recordsByBusinessId = employersBusinessIds.map((businessId) => {
      let employer = employersArray.find(
        (employer) =>
          employer.business_id === businessId &&
          employer.businessTypeEnum === 'EMPLOYER'
      );

      if (!employer) {
        employer = { business_id: businessId, name: 'MyLixani' };
      }

      // records resources employer business id matches employers business id
      const recordsOfEmployer = records
        .filter((record) => record.resource)
        .filter((record) => {
          const recordsResource = resources[record.resource];

          const employerBusinessId =
            recordsResource && recordsResource.employer
              ? recordsResource.employerBusinessId
              : 'MyLixani';

          return businessId === employerBusinessId;
        });

      return {
        businessId: employer.business_id,
        name: employer.name,
        records: recordsOfEmployer,
      };
    });

    return recordsByBusinessId;
  }
);

export const selectRecordsTotalHoursByAssignments = createSelector(
  [
    selectWorkCalendarFilteredRecords,
    selectSelectedRecordsIds,
    selectAssignmentsArray,
  ],
  (allRecords, selectedRecordsIds, assignments) => {
    const records =
      selectedRecordsIds.length > 0
        ? allRecords.filter((record) => selectedRecordsIds.includes(record.id))
        : allRecords;

    const recordTotalHoursByAssignments = assignments.map((assignment) => {
      const assignmentId = assignment.id;

      const assignmentsRecords = records.filter(
        (record) => record.assignment === assignmentId
      );

      const hours = getTotalClockedAndBilledHours(assignmentsRecords);

      return {
        name: `${nestedAssignmentNumber(assignment, assignments)}. ${
          assignment.name
        }`,
        hoursClocked: hours.clockedHours,
        hoursBilled: hours.billedHours,
        id: assignment.id,
      };
    });

    return recordTotalHoursByAssignments.filter(
      (data) => data.hoursClocked > 0
    );
  }
);

export const selectAllRecordsTotalHours = createSelector(
  [
    selectWorkCalendarFilteredRecords,
    selectSelectedRecordsIds,
    selectProjectsDataResourceEntities,
  ],
  (allRecords, selectedRecordsIds, resources) => {
    const workRecordsWithFoundResource = allRecords.filter(
      (record) =>
        (record.type === RECORD_TYPE.WORK ||
          record.type === RECORD_TYPE.PROJECTWORK) &&
        resources[record.resource]
    );

    const records =
      selectedRecordsIds.length > 0
        ? allRecords.filter((record) => selectedRecordsIds.includes(record.id))
        : workRecordsWithFoundResource;

    return getTotalClockedAndBilledHours(records);
  }
);

const getTotalClockedAndBilledHours = (records) => {
  let clockedHours = 0;
  let billedHours = 0;

  records.forEach((record) => {
    if (record.isDateOnlyRecord) {
      return;
    }
    const clockInAt = dayjs(record.clockInAt);
    const clockOutAt = dayjs(record.clockOutAt);
    const billedInAt = dayjs(record.billedClockInAt);
    const billedOutAt = dayjs(record.billedClockOutAt);
    const clockedInRecordMaxHours = 8.5;

    const clockDifference = clockOutAt.diff(clockInAt, 'minutes') / 60;
    //const billDifference = BilledOutAt.diff(BilledInAt, 'minutes') / 60;

    // if record is clocked in, add only max hours to total count
    if (record.clockOutAt) {
      clockedHours += calculateRecordHours(clockInAt, clockOutAt);
    } else if (clockDifference >= clockedInRecordMaxHours) {
      clockedHours += clockedInRecordMaxHours;
    } // if billed clocked out, add to total count. There should not be billed in records.
    if (record.billedClockOutAt) {
      billedHours += calculateRecordHours(billedInAt, billedOutAt);
    }
  });

  return { clockedHours, billedHours };
};

const calculateRecordHours = (clockInAt, clockOutAt) => {
  const recordHours = clockOutAt.diff(clockInAt, 'hours', true);

  return recordHours;
};

export const selectRecordsTotalHoursByBusinessId = createSelector(
  [selectRecordsByBusinessId],
  (businessIdRecordsObjects) => {
    return businessIdRecordsObjects
      .map((object) => {
        const hours = getTotalClockedAndBilledHours(object.records);

        return {
          businessId: object.businessId,
          name: object.name,
          hoursClocked: hours.clockedHours,
          hoursBilled: hours.billedHours,
        };
      })
      .filter((data) => data.hoursClocked > 0);
  }
);

export const selectFilteredRecordsTotalLength = createSelector(
  [selectWorkCalendarFilteredRecords],
  (records) => records.filter((record) => !record.isDateOnlyRecord).length
);

export const selectProjectAndChildProjectEditableRecords = createSelector(
  [
    selectSelectedProjectAndChildProjectAndResourceHasSelectedProjectAsEmployerRecords,
    selectUserProjectPermissions,
    selectProjects,
    selectResourceDefaultPermissions,
    selectCurrentUser,
    selectProjectsDataResourceEntities,
    selectUserAdminBusinessIds,
  ],
  (
    records,
    userProjectPermissions,
    projects,
    resourceDefaultPermissions,
    user,
    resources,
    adminBusinessIds
  ) => {
    const editableRecords = records
      .filter((record) => {
        const recordProject =
          projects && record ? projects[record.project] : {};

        const isOwnerOfRecordProject = recordProject
          ? recordProject.mirrorUser === user.id
          : false;

        const nearestUserProjectPermissionsForRecord =
          getNearestUserProjectPermissions(
            projects,
            userProjectPermissions,
            record.project
          );

        const defaultPermissionsWithNearestProjectPermissions = {
          ...resourceDefaultPermissions,
          ...nearestUserProjectPermissionsForRecord,
        };

        const isOwnRecord = record.user === user.id;

        const isPermissionToEdit = isOwnRecord
          ? defaultPermissionsWithNearestProjectPermissions[
              RECORDS_EDIT_OWN_RECORDS
            ]
          : defaultPermissionsWithNearestProjectPermissions[
              EDIT_OTHER_USERS_RECORDS
            ];

        const isRecordCurrentlyClockedIn = record.status === 'IN';
        const isRecordPartOfApprovalGroup = record.groupCode ? true : false;

        const selectedRecordResource = record ? resources[record.resource] : {};

        const employerBusinessId =
          selectedRecordResource && selectedRecordResource.employerBusinessId
            ? selectedRecordResource.employerBusinessId
            : undefined;

        const isAdminOfRecordResource = selectedRecordResource
          ? adminBusinessIds.includes(employerBusinessId)
          : false;

        const selectedRecordProject = record ? projects[record.project] : {};

        const employerBisnesIdByProject = selectedRecordProject
          ? selectedRecordProject.business_id
            ? selectedRecordProject.business_id
            : selectedRecordProject.masterParent
              ? projects[selectedRecordProject.masterParent].business_id
              : undefined
          : undefined;

        const isAdminOfRecordProject = selectedRecordProject
          ? adminBusinessIds.includes(employerBisnesIdByProject)
          : false;

        return (
          (isOwnerOfRecordProject ||
            isPermissionToEdit ||
            isAdminOfRecordResource ||
            isAdminOfRecordProject) &&
          !isRecordCurrentlyClockedIn &&
          !isRecordPartOfApprovalGroup
        );
      })
      .map((record) => record.id);
    return editableRecords;
  }
);

export const selectIsSelectedRecordsEditable = createSelector(
  [selectSelectedRecordsIds, selectProjectAndChildProjectEditableRecords],
  (selectedRecordsIds, editableRecordsIds) => {
    return selectedRecordsIds.every((recordId) =>
      editableRecordsIds.includes(recordId)
    );
  }
);

export const selectProjectAndChildProjectResourcesWithoutJoinedData =
  createSelector(
    [selectProjectsDataResourcesArray, selectProjects, selectSelectedProjectId],
    (resources, projects, selectedProjectId) => {
      const projectAndChildProjectIds = returnProjectIdAndItsChildProjectIds(
        [selectedProjectId],
        projects
      );

      return resources.filter((resource) =>
        projectAndChildProjectIds.includes(resource.project)
      );
    }
  );

export const selectExternalProjectOptions = createSelector(
  selectProjectsOfSelectedProject,
  selectEmployerProjectsArray,
  selectSelectedProjectId,
  (projects, employers, selectedProjectId) => {
    const allProjects = [...projects, ...employers];

    const projectAndChildProjectIds = returnProjectIdAndItsChildProjectIds(
      [selectedProjectId],
      allProjects
    );

    const options = projects
      .filter((project) => !projectAndChildProjectIds.includes(project.id))
      .map((project) => {
        const employer = employers.find(
          (employer) => project.business_id === employer.business_id
        );

        return {
          id: project.id,
          label: `${project.name} ${
            project.projectKey ? ` (${project.projectKey})` : ''
          }`,
          projectKey: project.projectKey,
          type: employer ? employer.name : project.name,
          name: project.name,
        };
      });

    options.sort((a, b) => sort.sortByKey(a, b, 'label'));

    return uniqBy(options, 'id');
  }
);

export const selectSelectedProjectAndChildProjectOptions = createSelector(
  selectProjectsOfSelectedProject,
  selectEmployerProjectsArray,
  selectSelectedProjectId,
  (projects, employers, selectedProjectId) => {
    const allProjects = [...projects, ...employers];
    const projectAndChildProjectIds = returnProjectIdAndItsChildProjectIds(
      [selectedProjectId],
      allProjects
    );

    const options = projects
      .filter((project) => projectAndChildProjectIds.includes(project.id))
      .map((project) => {
        return {
          id: project.id,
          label: `${project.name} ${
            project.projectKey ? ` (${project.projectKey})` : ''
          }`,
          projectKey: project.projectKey,
          parent: project.parent,
          name: project.name,
          type: 'priority',
        };
      });

    options.sort((a, b) => sort.sortByKey(a, b, 'label'));

    return uniqBy(options, 'id');
  }
);

// these are projects that are created under employer project.
export const selectPersonalWorkCalendarProjectsOptionsWhichHaveEmployer =
  createSelector(
    [selectProjectsThatUserHasResourceToOrIsCreatorOf],
    (projects) => {
      const options = projects
        .filter(
          (project) =>
            // filter out myLixani projects
            !!project.masterParent.id
        )
        .map((project) => {
          return {
            id: project.id,
            label: `${project.name} ${
              project.projectKey ? ` (${project.projectKey})` : ''
            }`,
            projectKey: project.projectKey,
            name: project.name,

            type:
              project.masterParent && project.masterParent.id
                ? project.masterParent.name
                : project.name,
          };
        });

      options.sort((a, b) => sort.sortByKey(a, b, 'label'));
      return uniqBy(options, 'id');
    }
  );

export const selectPersonalWorkCalendarMyLixaniProjectsOptions = createSelector(
  selectProjectsThatUserHasResourceToOrIsCreatorOf,
  (projects) => {
    const options = projects
      .filter(
        (project) =>
          !project.masterParent.id && project.businessTypeEnum === 'NORMAL'
      )
      .map((project) => {
        return {
          id: project.id,
          label: `${project.name} ${
            project.projectKey ? ` (${project.projectKey})` : ''
          }`,
          projectKey: project.projectKey,
          parent: project.parent,
          name: project.name,
          type: 'priority',
        };
      });

    options.sort((a, b) => sort.sortByKey(a, b, 'label'));
    return uniqBy(options, 'id');
  }
);

export const selectPersonalWorkCalendarProjectOptions = createSelector(
  [
    selectPersonalWorkCalendarProjectsOptionsWhichHaveEmployer,
    selectPersonalWorkCalendarMyLixaniProjectsOptions,
  ],
  (projectsWithEmployers, myLixaniProjects) => [
    ...projectsWithEmployers,
    ...myLixaniProjects,
  ]
);

export const selectAllProjectOptions = createSelector(
  selectExternalProjectOptions,
  selectSelectedProjectAndChildProjectOptions,
  selectPersonalWorkCalendarProjectOptions,
  selectIsPersonalWorkCalendarMode,

  (
    externalProjects,
    ownProjects,
    personalProjects,
    isPersonalWorkCalendarMode
  ) => {
    if (isPersonalWorkCalendarMode) {
      return personalProjects;
    } else {
      return [...externalProjects, ...ownProjects];
    }
  }
);

export const selectCurrentProjectOnlyProjectOption = createSelector(
  selectSelectedProjectId,
  selectAllProjectOptions,
  (currentProjectId, projectOptions) => {
    if (projectOptions.length < 1) {
      return true;
    } else {
      return projectOptions.length === 1 &&
        projectOptions[0].id === currentProjectId
        ? true
        : false;
    }
  }
);

export const selectResourceUserOptions = createSelector(
  selectWorkCalendarResourcesArray,
  selectSelectedProjectIds,
  selectSelectedEmployersBusinessIds,
  selectEmployerProjectsArray,
  (resources, selectedProjectIds, selectedEmployersBusinessIds, employers) => {
    const isSelectedProjects = selectedProjectIds.length > 0;
    const isSelectedEmployers = selectedEmployersBusinessIds.length > 0;
    const resourceOptionsUniqued = uniqueResourcesByUserAndEmployer(
      resources.filter(
        (resource) =>
          resource.project &&
          (isSelectedProjects
            ? selectedProjectIds.includes(resource.project)
            : true) &&
          (isSelectedEmployers
            ? selectedEmployersBusinessIds.includes(resource.employerBusinessId)
            : true)
      )
    );

    const resourceOptions = resourceOptionsUniqued.map((resource) => {
      const employerBusinessId = resource.employerBusinessId;

      const employer = employers.find(
        (employerProject) => employerProject.business_id === employerBusinessId
      );

      let employerName = employer && employer.name ? employer.name : 'MyLixani';

      return {
        id: resource.user,
        label: `${resource.lastName} ${resource.firstName}`,
        type: employerName,
      };
    });

    resourceOptions.sort((a, b) => sort.sortByKey(a, b, 'type'));

    return resourceOptions;
  }
);

export const selectEmployerOptions = createSelector(
  selectEmployerProjectsArray,
  selectProjectsDataResourcesArray,
  selectSelectedProjectIds,
  (employers, resources, selectedProjectIds) => {
    const isSelectedProjects = selectedProjectIds.length > 0;

    const resourcesEmployerBusinessIds = resources
      .filter(
        (resource) =>
          resource.employer &&
          (isSelectedProjects
            ? selectedProjectIds.includes(resource.project)
            : true)
      )
      .map((resource) => resource.employerBusinessId);

    let employerOptions = uniqBy(
      employers.filter((employer) =>
        resourcesEmployerBusinessIds.includes(employer.business_id)
      ),
      'business_id'
    ).map((employer) => {
      return {
        value: employer.business_id,
        label: employer.name,
        type: 'all',
      };
    });

    employerOptions.sort((a, b) => sort.sortByKey(a, b, 'label'));

    return employerOptions;
  }
);

export const selectProjectOptionsWithAssignments = createSelector(
  selectAllProjectOptions,
  selectAssignmentsArrayWithoutDeleted,
  selectSelectedProjectIds,
  (projectOptions, assignments, selectedProjectIds) => {
    const assignmentsProjectIds = assignments.map((a) => a.project);

    const isSelectedProjects = selectedProjectIds.length > 0;

    const projectOptionsWithAssignments = projectOptions.filter(
      (projectOption) =>
        assignmentsProjectIds.includes(projectOption.id) &&
        (isSelectedProjects
          ? selectedProjectIds.includes(projectOption.id)
          : true)
    );

    return projectOptionsWithAssignments;
  }
);

export const selectNestedAssignmentOptionsByProject = createSelector(
  selectAssignmentsArrayWithoutDeleted,
  selectProjects,
  selectSelectedProjectIds,
  selectAllProjectOptions,
  (_, args) => args,

  (assignments, projects, selectedProjectIds, projectOptions, args) => {
    const projectId = args;

    const isSelectedProjects = selectedProjectIds.length > 0;

    const projectOptionsIds = projectOptions.map(
      (projectOption) => projectOption.id
    );

    let assignmentOptions = assignments
      .filter(
        (assignment) =>
          (isSelectedProjects
            ? selectedProjectIds.includes(assignment.project)
            : true) &&
          projectOptionsIds.includes(assignment.project) &&
          assignment.project === projectId
      )
      .map((assignment) => {
        const assignmentProjectName =
          projects[assignment.project] && projects[assignment.project].name
            ? projects[assignment.project].name
            : '';
        return {
          id: assignment.id,
          value: assignment.id,
          parent: assignment.parent ? assignment.parent : false,
          label: `${nestedAssignmentNumber(assignment, assignments)}. ${
            assignment.name
          }`,
          type: assignmentProjectName,
          project: assignment.project,
          number: assignment?.number,
        };
      });
    assignmentOptions.sort((a, b) => {
      const numA = parseFloat(a.number) || 0;
      const numB = parseFloat(b.number) || 0;
      return numA - numB;
    });

    const assignmentOptionsParents = assignmentOptions.filter((a) => !a.parent);

    const nestedAssignmentOptions = nestedAssigmentTree(
      assignmentOptionsParents,
      assignmentOptions
    );

    return nestedAssignmentOptions;
  }
);

export const selectWorkCalendarSearchFilters = createSelector(
  [
    selectSelectedEmployersBusinessIds,
    selectSelectedProjectIds,
    selectSelectedUserIds,
  ],
  (businessIds, projectIds, userIds) => ({
    businessIds,
    projectIds,
    userIds,
  })
);

export const selectWorkCalendarSearchFiltersWithAssignmentsAndProject =
  createSelector(
    [
      selectWorkCalendarSearchFilters,
      selectSelectedAssignmentIds,
      selectAssignmentsArrayWithoutDeleted,
      selectProjects,
    ],
    (searchFilters, selectedAssignmentIds, selectedAssignments, projects) => {
      return {
        ...searchFilters,
        projects:
          searchFilters.projectIds && searchFilters.projectIds.length
            ? searchFilters.projectIds.map((projectId) => projects[projectId])
            : [],
        assignmentIds: selectedAssignmentIds,
        assignments: selectedAssignments.filter((assignment) =>
          selectedAssignmentIds.includes(assignment.id)
        ),
      };
    }
  );

export const selectCurrentProjectAndSubProjects = createSelector(
  [selectProjects, selectSelectedProject],
  (projects, selectedProject) => {
    const projectAndChildProjectIds = returnProjectIdAndItsChildProjectIds(
      [selectedProject.id],
      projects
    );
    return {
      projects: projectAndChildProjectIds.map(
        (projectId) => projects[projectId]
      ),
    };
  }
);

export const selectIsActiveSearchFilters = createSelector(
  [selectWorkCalendarSearchFilters],
  (searchFilters) => {
    const { businessIds, projectIds, userIds } = searchFilters;

    return businessIds.length > 0 || projectIds.length > 0 || userIds.length > 0
      ? true
      : false;
  }
);

export const selectWorkCalendarProjectsArrayWithJoinedData = createSelector(
  [
    selectWorkCalendarProjectsArray,
    selectEmployerProjectsArray,
    selectProjects,
    selectEmployerProjectEntities,
  ],
  (projectsArray, employersArray, projectsEntities, employersEntities) => {
    const allProjectsArray = uniqBy(
      [...projectsArray, ...employersArray],
      'id'
    );
    const allProjectEntities = { ...projectsEntities, ...employersEntities };

    const joinedDataProjects = allProjectsArray.map((project) => ({
      ...project,
      masterParent: allProjectEntities[project.masterParent]
        ? allProjectEntities[project.masterParent]
        : { id: project.masterParent },
      parent: allProjectEntities[project.parent]
        ? allProjectEntities[project.parent]
        : { id: project.parent },
    }));
    return joinedDataProjects;
  }
);

export const selectWorkCalendarResourcesArrayWithJoinedData = createSelector(
  [selectProjectsDataResourcesArray, selectEmployerProjectEntities],
  (resources, employers) =>
    resources.map((resource) => ({
      ...resource,
      employer: employers[resource.employer]
        ? employers[resource.employer]
        : { id: resource.employer },
    }))
);

export const selectCurrentProjectsAndChildProjectsResources = createSelector(
  [
    selectWorkCalendarResourcesArrayWithJoinedData,
    selectSelectedProject,
    selectWorkCalendarProjectsArrayWithJoinedData,
  ],
  (resources, selectedProject, projects) => {
    const childProjectIds = projects
      .filter(
        (project) =>
          project.masterParent.id === selectedProject.id ||
          project.parent.id === selectedProject.id
      )
      .map((project) => project.id);

    return resources.filter((resource) => {
      const isResourceEmployerProjectEmployerProject =
        resource.employer.businessTypeEnum === 'EMPLOYER';
      const resourceHasEmployer = resource.employer;
      const isResourceFromSelectedProject =
        resource.project === selectedProject.id;
      const isResourceFromChildProjects = childProjectIds.includes(
        resource.project
      );

      return (
        (isResourceFromSelectedProject || isResourceFromChildProjects) &&
        (isResourceEmployerProjectEmployerProject || !resourceHasEmployer)
      );
    });
  }
);

export const selectCurrentProjectsAndChildProjectsResourcesUnique =
  createSelector(
    [selectCurrentProjectsAndChildProjectsResources],
    (resources) => {
      const uniqueResources = uniqWith(
        resources,
        (a, b) =>
          a.user === b.user && a.employerBusinessId === b.employerBusinessId
      );

      const uniqueAndSortedResource = orderBy(
        uniqueResources,
        [
          (resource) =>
            resource.employer && resource.employer.name
              ? resource.employer.name.toLowerCase()
              : 'myLixani'.toLowerCase(),
        ],
        ['asc']
      );

      return uniqueAndSortedResource;
    }
  );

function nestedName(project, projects) {
  //adds parent names to return value until master parent (employer) name is found. example of final return value for when project param is a subproject:
  // "employerName / projectName / subprojectName"
  let name = project.name;
  if (project.parent) {
    let parent = projects.find((p) => p.id === project.parent);
    if (parent) {
      name = nestedName(parent, projects) + ' / ' + name;
    }
  }
  return name;
}

function nestedAssignmentNumber(assignment, assignments) {
  let number = assignment.number;
  if (assignment.parent) {
    let parent = assignments.find((p) => p.id === assignment.parent);
    if (parent) {
      number = nestedAssignmentNumber(parent, assignments) + '.' + number;
    }
  }
  return number;
}

function nestedAssigmentTree(assignmentParents, allAssignments) {
  function nestedAssignments(parentAssignment, assignments) {
    const assignmentChildren = assignments.filter(
      (a) => a.parent === parentAssignment.id
    );
    const assignmentHasChildren = assignmentChildren.length > 0;

    let children = [];

    if (assignmentHasChildren) {
      children = assignmentChildren.map((a) =>
        nestedAssignments(a, assignments)
      );
    }
    let assignmentTree = {
      assignment: parentAssignment,
      children,
    };

    return assignmentTree;
  }

  const nestedAssignmentOptions = assignmentParents.map((a) =>
    nestedAssignments(a, allAssignments)
  );

  return nestedAssignmentOptions;
}

export const selectWorkCalendarRecordIdsUserIsAdminOf = createSelector(
  [selectWorkCalendarRecordsArray, selectProjects, selectUserAdminBusinessIds],
  (records, workCalendarProjects, userAdminBusinessIds) => {
    return records
      .filter((record) => {
        const projectOfRecord = workCalendarProjects[record.project];

        return projectOfRecord
          ? userAdminBusinessIds.includes(projectOfRecord.business_id)
          : false;
      })
      .map((record) => record.id);
  }
);

export const selectSelectedWorkCalendarEmployer = createSelector(
  [selectSelectedEmployersBusinessIds, selectEmployerProjectsArray],
  (selectedBusinessIds, employers) =>
    employers.find(
      (employer) => employer.business_id === selectedBusinessIds[0]
    )
);

export const selectRecordsMassEditProjectListOptions = createSelector(
  [selectWorkCalendarProjectsArray],
  (projects) => {
    const effectiveProjects = projects.filter(
      (project) => project.status !== 'ARCHIVED' && project.type !== 'EMPLOYER'
    );

    return orderBy(
      effectiveProjects,
      [(project) => project.name.toLowerCase()],
      'asc'
    );
  }
);
